import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getMe, getTodayStats, getLeaderboard, getDailyPublic, getRebecaGuest } from "@/lib/api";
import { dailyChallengeNumber } from "@/lib/share";
import RebecaHomeCard from "@/components/RebecaHomeCard";

export default function OnboardingPage() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [dailyPublic, setDailyPublic] = useState(null);
  const [rebecaHome, setRebecaHome] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        await getMe();
        try {
          const pendingSeo = localStorage.getItem("pending_seo_slug");
          if (pendingSeo) {
            localStorage.removeItem("pending_seo_slug");
            navigate(`/play/debates/${pendingSeo}`, { replace: true });
            return;
          }
        } catch (_e) {}
        // Check if user was mid-challenge before login
        try {
          const pending = localStorage.getItem("pending_challenge");
          if (pending) {
            localStorage.removeItem("pending_challenge");
            navigate(`/challenge/${pending}`, { replace: true });
            return;
          }
        } catch (_e) {}
        navigate("/game", { replace: true });
        return;
      } catch (_e) {
        setChecking(false);
      }
      // Fetch today stats + leaderboard (public endpoints, no auth needed)
      try {
        const [s, lb, daily, rebeca] = await Promise.allSettled([
          getTodayStats(),
          getLeaderboard(),
          getDailyPublic(),
          getRebecaGuest(),
        ]);
        if (s.status === "fulfilled") setStats(s.value);
        if (lb.status === "fulfilled") setLeaderboard(lb.value.slice(0, 3));
        if (daily.status === "fulfilled") setDailyPublic(daily.value);
        if (rebeca.status === "fulfilled") setRebecaHome(rebeca.value);
      } catch (_e) { /* ignore */ }
    })();
  }, [navigate]);

  const handlePlay = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`;
  };

  const handleDemo = () => {
    navigate("/demo");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-display text-2xl uppercase">Cargando...</div>
      </div>
    );
  }

  const winRate = stats?.win_rate;
  const totalDebates = stats?.total_debates ?? 0;

  return (
    <div className="min-h-screen px-4 sm:px-6 py-8 md:py-10 flex flex-col">
      <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col gap-6 justify-center">

        {/* Logo */}
        <motion.div
          initial={{ y: -16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="text-center"
        >
          <h1 className="font-display text-5xl sm:text-7xl md:text-8xl uppercase leading-[0.9]">
            Conven<span className="text-[#FF4C4C]">ce</span>me
          </h1>
          <p className="font-mono-cm text-xs uppercase tracking-widest text-[#101010]/50 mt-2">
            ¿Podés callar a una IA sarcástica en 60 segundos?
          </p>
        </motion.div>

        {/* REBECA card — protagonista */}
        {rebecaHome && (
          <RebecaHomeCard
            message={rebecaHome.message}
            ctaHint={rebecaHome.cta_hint}
            mood={rebecaHome.mood}
            testId="onboarding-rebeca-card"
          >
            <div className="flex items-center gap-3 flex-wrap mt-4">
              {winRate !== null && winRate !== undefined ? (
                <div className="bg-[#FF4C4C] border-2 border-[#FF4C4C]/50 rounded-lg px-3 py-1.5">
                  <span className="font-display text-white text-lg">{winRate}%</span>
                  <span className="font-mono-cm text-[10px] text-white/70 uppercase tracking-widest ml-1">
                    me ganó hoy
                  </span>
                </div>
              ) : (
                <div className="bg-white/10 rounded-lg px-3 py-1.5">
                  <span className="font-mono-cm text-[10px] text-white/60 uppercase tracking-widest">
                    Sé el primero en intentarlo hoy
                  </span>
                </div>
              )}
              {totalDebates > 0 && (
                <div className="bg-white/10 rounded-lg px-3 py-1.5">
                  <span className="font-mono-cm text-[10px] text-white/60 uppercase tracking-widest">
                    {totalDebates} debate{totalDebates !== 1 ? "s" : ""} hoy
                  </span>
                </div>
              )}
            </div>
          </RebecaHomeCard>
        )}

        {/* Reto del día — protagonista viral */}
        {dailyPublic && (
          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="border-4 border-[#101010] rounded-2xl cm-shadow overflow-hidden bg-gradient-to-br from-[#FFDE59] via-[#FFDE59] to-[#FF4C4C]"
            data-testid="landing-daily-card"
          >
            <div className="bg-[#101010] px-5 py-2 flex items-center justify-between gap-2">
              <span className="font-mono-cm text-[11px] uppercase tracking-widest text-[#FFDE59] font-bold">
                🔥 Reto del día #{dailyChallengeNumber(dailyPublic.date)}
              </span>
              <span className="font-mono-cm text-[10px] text-white/70 uppercase tracking-widest">
                {dailyPublic.players_today} jugaron hoy
              </span>
            </div>
            <div className="p-5 md:p-6">
              <p className="font-display uppercase text-xl md:text-2xl leading-tight mb-4">
                &ldquo;{dailyPublic.stance}&rdquo;
              </p>
              <p className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60 mb-4">
                Express · 1 ronda · 60 seg · misma postura para todos
              </p>
              <button
                onClick={handleDemo}
                className="cm-press w-full bg-[#101010] text-[#FFDE59] border-4 border-[#101010] rounded-xl px-6 py-4 font-display uppercase text-lg cm-shadow-sm"
                data-testid="landing-daily-play-button"
              >
                Probar el reto de hoy →
              </button>
            </div>
          </motion.div>
        )}

        {/* Cómo funciona — compacto */}
        <motion.div
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="grid grid-cols-3 gap-3"
        >
          {[
            { n: "1", label: "REBECA elige una postura polémica", color: "#FFDE59" },
            { n: "2", label: "Vos argumentás en 60 segundos", color: "#A8FF76" },
            { n: "3", label: "Necesitás 75+ para ganarle", color: "#FF4C4C" },
          ].map((s) => (
            <div
              key={s.n}
              className="border-4 border-[#101010] rounded-xl p-3 text-center cm-shadow"
              style={{ background: s.color }}
            >
              <div className="font-display text-3xl mb-1">{s.n}</div>
              <div className="font-mono-cm text-[10px] uppercase tracking-widest leading-tight">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Mejora 8: preview de posturas reales */}
        <motion.div
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.38, duration: 0.4 }}
        >
          <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/40 mb-2 text-center">
            Ejemplos de lo que defiende REBECA
          </div>
          <div className="space-y-2">
            {[
              "El asado del domingo es una obligación social que nadie disfruta de verdad",
              "La pizza argentina es objetivamente mejor que la italiana",
              "Los que piden bien la pizza son los peores compañeros de trabajo",
            ].map((stance, i) => (
              <div key={i} className="border-4 border-[#101010] rounded-xl px-4 py-3 bg-white cm-shadow-sm flex items-center gap-3">
                <span className="font-display text-[#FF4C4C] text-lg shrink-0">REBECA:</span>
                <span className="font-medium text-sm italic">&ldquo;{stance}&rdquo;</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Mejora 8: top 3 del leaderboard en vivo */}
        {leaderboard.length > 0 && (
          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.46, duration: 0.4 }}
            className="border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden"
          >
            <div className="bg-[#101010] px-4 py-2.5 flex items-center gap-2">
              <span className="text-base">🏆</span>
              <span className="font-mono-cm text-[11px] uppercase tracking-widest text-white/70">
                Top 3 que le ganaron a REBECA
              </span>
            </div>
            <div className="divide-y-4 divide-[#101010]">
              {leaderboard.map((e, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 bg-white">
                  <span className="text-xl">{["🥇","🥈","🥉"][i]}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display uppercase text-sm truncate">{e.name}</div>
                    <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50 italic truncate">
                      &ldquo;{e.stance}&rdquo;
                    </div>
                  </div>
                  <div className="font-display text-2xl tabular-nums">{e.score}</div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.45, duration: 0.4 }}
          className="flex flex-col items-center gap-3"
        >
          <button
            onClick={handleDemo}
            data-testid="onboarding-demo-button"
            className="cm-press w-full max-w-sm bg-[#FF4C4C] text-white border-4 border-[#101010] rounded-xl px-10 py-5 font-display uppercase text-2xl md:text-3xl cm-shadow"
          >
            Probar gratis →
          </button>
          <button
            onClick={handlePlay}
            data-testid="onboarding-play-button"
            className="cm-press w-full max-w-sm bg-white text-[#101010] border-4 border-[#101010] rounded-xl px-10 py-4 font-display uppercase text-lg md:text-xl cm-shadow-sm"
          >
            Entrar con Google
          </button>
          <p className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/40 text-center max-w-sm">
            La demo no requiere cuenta · 1 ronda express · reto del día
          </p>
        </motion.div>

      </div>

      <footer className="text-center text-xs font-mono-cm uppercase tracking-widest text-[#101010]/30 mt-8">
        Convenceme.app · 2026
      </footer>
    </div>
  );
}
